import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { cn } from "@/modules/lib/utils"
import { InputGroup, InputGroupAddon } from "@/components/ui/baseUi/input-group"
import { SearchIcon, XIcon } from "lucide-react"
import { word } from "@/modules/core/utils/i18n"
import { TemplateType } from "@/modules/template/types/templateType"
import { useTemplateImportActions, useTemplateImportSearchTerm } from "@/modules/template/stores/useTemplateImportStore"

type TemplateSearchProps = {
  templateType: TemplateType
  className?: string
} & React.ComponentProps<"div">

export function TemplateSearch({ templateType, className, ...props }: TemplateSearchProps) {
  const searchTerm = useTemplateImportSearchTerm()
  const { setSearchTerm } = useTemplateImportActions()
  const defaultValues = {
    searchTerm: searchTerm,
  }

  const form = useAppForm({
    defaultValues: {
      ...defaultValues,
    },
  })

  let placeholder: string
  switch (templateType) {
    case TemplateType.WEEK:
      placeholder = word("templateType.search.placeholder.week")
      break
    case TemplateType.SESSION:
      placeholder = word("templateType.search.placeholder.session")
      break
    case TemplateType.EXERCISE:
      placeholder = word("templateType.search.placeholder.exercise")
      break
    default: {
      const exhaustiveCheck: never = templateType
      throw new Error(`Invalid template type ${String(exhaustiveCheck)}`)
    }
  }

  return (
    <form.AppField
      name="searchTerm"
      validators={{
        onChangeAsyncDebounceMs: 350,
        onChangeAsync: async ({ value }) => {
          await new Promise((resolve) => setTimeout(resolve, 0))
          setSearchTerm(value)
        },
      }}
    >
      {(field) => (
        <field.Field className={cn(className)} {...props}>
          <field.Content>
            <InputGroup className="h-12">
              <field.InputGroupInput name="searchTerm" placeholder={placeholder} />
              <InputGroupAddon align="inline-start">
                <SearchIcon />
              </InputGroupAddon>
              {field.state.value && (
                <InputGroupAddon align="inline-end">
                  <XIcon
                    className="size-4 cursor-pointer"
                    onClick={() => {
                      field.handleChange("")
                    }}
                  />
                </InputGroupAddon>
              )}
            </InputGroup>

            <field.Message />
          </field.Content>
        </field.Field>
      )}
    </form.AppField>
  )
}
